import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@teals/ui-web';
import { useAuth } from '../../hooks/useAuth';
import { useOrgRole } from '../../hooks/useOrgRole';
import { useGetOrgModelsQuery } from '../../store/api/orgModelsApi';
import type { OrgModel } from '../../store/api/orgModelsApi';
import { useSendMessageMutation } from '../../store/api/chatApi';
import { modelSupportsEffort } from '../../utils/modelDisplay';
import {
  MAX_COMPARE,
  MODEL_PLAYGROUND_AGENT,
  canManageModels,
  canTryModels,
  createAgentPath,
  modelEnableBlocked,
  parseModelIds,
} from './modelsShared';
import {
  ActionBar,
  BackLink,
  Header,
  HeaderCopy,
  Meta,
  Muted,
  Notice,
  Page,
  Select,
  Subtitle,
  Title,
} from './modelsStyles';
import { useModelToggle } from './useModelToggle';

type Effort = 'low' | 'medium' | 'high';

type Turn = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  footer?: string;
};

type Column = {
  key: string;
  productId: string;
  turns: Turn[];
  sessionId?: string;
  pending: boolean;
  error: boolean;
  lastPrompt?: string;
};

const Settings = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: start;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 4rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.875rem;
  resize: vertical;
  box-sizing: border-box;
`;

const Columns = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ColumnCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 18rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--bg-surface);
`;

const ColumnHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  select {
    flex: 1;
    min-width: 0;
  }
`;

const Transcript = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  overflow-y: auto;
  max-height: 28rem;
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  align-self: ${(p) => (p.$role === 'user' ? 'flex-end' : 'stretch')};
  max-width: ${(p) => (p.$role === 'user' ? '85%' : '100%')};
  padding: 0.625rem 0.75rem;
  border-radius: 10px;
  background: ${(p) => (p.$role === 'user' ? 'var(--accent-subtle)' : 'var(--bg-elevated)')};
  color: var(--text-primary);
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const Composer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;

  textarea {
    flex: 1;
  }
`;

let keySeq = 0;
const nextKey = () => `col-${++keySeq}`;

function newColumn(productId: string): Column {
  return { key: nextKey(), productId, turns: [], pending: false, error: false };
}

const ModelPlayground: React.FC = () => {
  const navigate = useNavigate();
  const { productId: routeProductId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { role } = useOrgRole();
  const canManage = canManageModels(role);
  const canTry = canTryModels(role);
  const { data: models = [], isLoading } = useGetOrgModelsQuery(orgId || '', {
    skip: !orgId,
  });
  const { turnOn, patching } = useModelToggle(orgId);
  const [sendMessage] = useSendMessageMutation();

  const initialIds = useMemo(
    () => (routeProductId ? [routeProductId] : parseModelIds(searchParams.get('ids'))),
    // Only seed from the URL on first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [columns, setColumns] = useState<Column[]>(() => initialIds.map(newColumn));
  const [instructions, setInstructions] = useState('');
  const [effort, setEffort] = useState<Effort | ''>('');
  const [prompt, setPrompt] = useState('');

  const chatModels = useMemo(
    () =>
      models.filter(
        (m) =>
          m.chatCapable !== false ||
          m.task === 'image_generation' ||
          m.task === 'video_generation',
      ),
    [models],
  );
  const byId = useMemo(() => new Map(models.map((m) => [m.productId, m])), [models]);

  useEffect(() => {
    if (columns.length > 0 || chatModels.length === 0) return;
    const first = chatModels.find((m) => m.enabled) || chatModels[0];
    setColumns([newColumn(first.productId)]);
  }, [columns.length, chatModels]);

  const anyEffort = columns.some((c) =>
    modelSupportsEffort(byId.get(c.productId)?.capabilities),
  );

  const blockReason = (model?: OrgModel): string | null => {
    if (!model) return 'This model is not in the catalog.';
    if (
      model.chatCapable === false &&
      model.task !== 'image_generation' &&
      model.task !== 'video_generation'
    )
      return `${model.displayName} isn't for chat.`;
    if (!model.enabled) return 'off';
    return null;
  };

  const updateColumn = (key: string, patch: (c: Column) => Column) =>
    setColumns((cols) => cols.map((c) => (c.key === key ? patch(c) : c)));

  const runColumn = async (column: Column, text: string) => {
    const model = byId.get(column.productId);
    if (!model || blockReason(model)) return;
    const started = performance.now();
    updateColumn(column.key, (c) => ({
      ...c,
      pending: true,
      error: false,
      lastPrompt: text,
      turns: [...c.turns, { id: `${c.key}-u-${c.turns.length}`, role: 'user', content: text }],
    }));
    try {
      const trimmed = instructions.trim();
      const result = await sendMessage({
        query: text,
        agentName: MODEL_PLAYGROUND_AGENT,
        sessionId: column.sessionId,
        model: column.productId,
        ...(effort && modelSupportsEffort(model.capabilities) ? { effort } : {}),
        metadata: {
          ...(trimmed ? { prompt_context: { instructions: trimmed } } : {}),
          ...(model.task === 'image_generation' || model.task === 'video_generation'
            ? {
                media: {
                  aspect_ratio: model.task === 'video_generation' ? '16:9' : '1:1',
                  quality: 'standard',
                  ...(model.task === 'video_generation' ? { duration_seconds: 5 } : {}),
                },
              }
            : {}),
        },
      }).unwrap();
      const seconds = ((performance.now() - started) / 1000).toFixed(1);
      updateColumn(column.key, (c) => ({
        ...c,
        pending: false,
        sessionId: result.sessionId || c.sessionId,
        turns: [
          ...c.turns,
          {
            id: `${c.key}-a-${c.turns.length}`,
            role: 'assistant',
            content:
              result.media?.[0]?.url
                ? result.media[0].url
                : result.mediaJob?.jobId
                  ? `Video job ${result.mediaJob.status || 'queued'}`
                  : result.response || 'Still working on a reply…',
            footer: `${model.displayName} · ${seconds}s`,
          },
        ],
      }));
    } catch {
      updateColumn(column.key, (c) => ({ ...c, pending: false, error: true }));
    }
  };

  const handleSend = () => {
    const text = prompt.trim();
    if (!text || !canTry) return;
    setPrompt('');
    columns.forEach((column) => void runColumn(column, text));
  };

  const retry = (column: Column) => {
    if (!column.lastPrompt) return;
    updateColumn(column.key, (c) => ({
      ...c,
      error: false,
      turns: c.turns.slice(0, -1),
    }));
    void runColumn({ ...column, turns: column.turns.slice(0, -1) }, column.lastPrompt);
  };

  const changeModel = (key: string, productId: string) =>
    updateColumn(key, () => ({ ...newColumn(productId), key }));

  const addColumn = () => {
    const used = new Set(columns.map((c) => c.productId));
    const next =
      chatModels.find((m) => m.enabled && !used.has(m.productId)) ||
      chatModels.find((m) => !used.has(m.productId));
    if (next) setColumns((cols) => [...cols, newColumn(next.productId)]);
  };

  const newChat = () =>
    setColumns((cols) => cols.map((c) => ({ ...newColumn(c.productId), key: c.key })));

  const anyPending = columns.some((c) => c.pending);
  const runnable = columns.some((c) => !blockReason(byId.get(c.productId)));

  if (!orgId) return null;

  return (
    <Page>
      <BackLink type="button" onClick={() => navigate('/models')}>
        ← Models
      </BackLink>
      <Header>
        <HeaderCopy>
          <Title>Model playground</Title>
          <Subtitle>
            Try models with your own prompt before building an agent. Chats here
            use your organisation&apos;s usage.
          </Subtitle>
        </HeaderCopy>
        <ActionBar>
          <Button variant="ghost" onClick={newChat} disabled={anyPending}>
            New chat
          </Button>
          {columns.length < MAX_COMPARE && canTry && (
            <Button variant="outline" onClick={addColumn} disabled={anyPending}>
              + Compare another model
            </Button>
          )}
        </ActionBar>
      </Header>

      {!canTry && (
        <Notice $tone="warning" role="status">
          Viewers can browse models but can&apos;t try them. Ask an Owner or Admin
          for Builder access.
        </Notice>
      )}

      <Settings>
        <Label>
          Instructions (optional)
          <TextArea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="For example: You are a friendly support agent for Acme. Keep answers under 100 words."
            disabled={!canTry}
          />
        </Label>
        {anyEffort && (
          <Label>
            Effort
            <Select
              value={effort}
              onChange={(e) => setEffort(e.target.value as Effort | '')}
              disabled={!canTry}
            >
              <option value="">Default</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </Select>
          </Label>
        )}
      </Settings>

      {isLoading ? (
        <Muted>Loading models…</Muted>
      ) : (
        <Columns $count={Math.max(columns.length, 1)}>
          {columns.map((column) => {
            const model = byId.get(column.productId);
            const reason = blockReason(model);
            return (
              <ColumnCard key={column.key} data-testid={`playground-column-${column.productId}`}>
                <ColumnHead>
                  <Select
                    aria-label="Model"
                    value={column.productId}
                    onChange={(e) => changeModel(column.key, e.target.value)}
                    disabled={column.pending}
                  >
                    {!model && <option value={column.productId}>{column.productId}</option>}
                    {chatModels.map((m) => (
                      <option key={m.productId} value={m.productId}>
                        {m.displayName}
                        {m.enabled ? '' : ' (off)'}
                      </option>
                    ))}
                  </Select>
                  {columns.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${model?.displayName || column.productId}`}
                      onClick={() =>
                        setColumns((cols) => cols.filter((c) => c.key !== column.key))
                      }
                    >
                      ×
                    </Button>
                  )}
                </ColumnHead>

                {reason === 'off' && model ? (
                  <Notice role="status">
                    {model.displayName} is off for this organisation.{' '}
                    {canManage ? (
                      modelEnableBlocked(model) ? (
                        <Link to="/payments">Add a payment method to turn it on.</Link>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={patching}
                          onClick={() => void turnOn(model)}
                        >
                          Turn on and try
                        </Button>
                      )
                    ) : (
                      'Ask an Owner or Admin to turn it on.'
                    )}
                  </Notice>
                ) : reason ? (
                  <Notice role="status">{reason}</Notice>
                ) : null}

                <Transcript aria-live="polite">
                  {column.turns.length === 0 && !reason && (
                    <Muted>Send a prompt to see how this model replies.</Muted>
                  )}
                  {column.turns.map((turn) => (
                    <div key={turn.id}>
                      <Bubble $role={turn.role}>
                        {turn.role === 'assistant' && /^https?:|^memory:/.test(turn.content) ? (
                          <img src={turn.content} alt="" style={{ maxWidth: '100%' }} />
                        ) : (
                          turn.content
                        )}
                      </Bubble>
                      {turn.footer && <Meta style={{ marginTop: '0.25rem' }}>{turn.footer}</Meta>}
                    </div>
                  ))}
                  {column.pending && <Muted>Thinking…</Muted>}
                  {column.error && (
                    <Notice $tone="error" role="alert">
                      Couldn&apos;t get a reply.{' '}
                      <Button variant="ghost" size="sm" onClick={() => retry(column)}>
                        Retry
                      </Button>
                    </Notice>
                  )}
                </Transcript>

                {model && !reason && canTry && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(createAgentPath(model.productId, instructions))}
                  >
                    Create agent with this model
                  </Button>
                )}
              </ColumnCard>
            );
          })}
        </Columns>
      )}

      {columns.some((column) => byId.get(column.productId)?.task === 'video_generation') && (
        <Muted>Videos take a few minutes and can't be stopped once started.</Muted>
      )}

      <Composer>
        <TextArea
          aria-label="Prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={
            columns.some((column) => byId.get(column.productId)?.task === 'video_generation')
              ? 'Describe the clip…'
              : columns.some((column) => byId.get(column.productId)?.task === 'image_generation')
                ? 'Describe the image…'
                : columns.length > 1
                  ? 'Ask something — every model gets the same prompt'
                  : 'Ask something'
          }
          disabled={!canTry}
        />
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={!canTry || !prompt.trim() || anyPending || !runnable}
        >
          Send
        </Button>
      </Composer>
    </Page>
  );
};

export default ModelPlayground;
